import { useMemo } from "react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { getApiErrorMessage, isConflictError } from "@/lib/api/api";
import {
  useMenuItem,
  useCreateMenuItem,
  useUpdateMenuItem,
} from "@/hooks/menu-items";
import {
  CreateMenuItemDialog,
  type MenuItemInitialValue,
} from "@/components/recipes/dialogs/CreateMenuItemDialog";
import { toMenuItemInitialValue } from "@/components/recipes/dialogs/menu-item-form-value";

interface MenuItemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set to edit that menu item; null/undefined creates a new one. */
  editingId?: number | null;
  /** Category a new item lands in (ignored when editing). */
  defaultCategoryId?: number;
  /** Concept the user has no choice about. Hides the concept select. */
  companyId?: number;
  defaultCompanyId?: number | null;
  organizationId?: number | null;
  canSelectOrganization?: boolean;
  /** Offered when adding, if the dish the user wants isn't in the list. */
  onCreatePlatedRecipe?: () => void;
  /** Called after a successful create or update, once the dialog has closed. */
  onSaved?: () => void;
}

/**
 * The menu item create/edit dialog wired to its mutations. Shared by the
 * Menus module and the Recipes screen's Menu Items tab so both save, report
 * conflicts and fetch the item being edited the same way.
 */
export function MenuItemFormDialog({
  open,
  onOpenChange,
  editingId = null,
  defaultCategoryId,
  companyId,
  defaultCompanyId,
  organizationId,
  canSelectOrganization,
  onCreatePlatedRecipe,
  onSaved,
}: MenuItemFormDialogProps) {
  const { t, i18n } = useLingui();
  const createMenuItem = useCreateMenuItem();
  const updateMenuItem = useUpdateMenuItem();
  const { data: editingMenuItem } = useMenuItem(editingId ?? 0, editingId != null);

  const initial = useMemo<MenuItemInitialValue | undefined>(
    () =>
      editingId == null || !editingMenuItem
        ? undefined
        : toMenuItemInitialValue(editingMenuItem, i18n),
    [editingId, editingMenuItem, i18n],
  );

  return (
    <CreateMenuItemDialog
      // While the item being edited is still loading there is nothing to show.
      open={open && (editingId == null || !!initial)}
      onOpenChange={onOpenChange}
      initial={initial}
      defaultCategoryId={defaultCategoryId}
      companyId={companyId}
      defaultCompanyId={defaultCompanyId}
      organizationId={organizationId}
      canSelectOrganization={canSelectOrganization}
      isPending={createMenuItem.isPending || updateMenuItem.isPending}
      // Only when adding: switching away from an item being edited would
      // drop its unsaved changes.
      onCreatePlatedRecipe={editingId != null ? undefined : onCreatePlatedRecipe}
      onSave={(data) => {
        if (editingId != null) {
          updateMenuItem.mutate(
            { id: editingId, data },
            {
              onSuccess: () => {
                toast.success(t`Menu item updated`);
                onOpenChange(false);
                onSaved?.();
              },
              onError: (error) =>
                toast.error(
                  isConflictError(error)
                    ? t`A menu item named "${data.name}" already exists in this menu`
                    : getApiErrorMessage(error, t`Failed to update menu item`),
                ),
            },
          );
        } else {
          createMenuItem.mutate(data, {
            onSuccess: () => {
              toast.success(t`Menu item created`);
              onOpenChange(false);
              onSaved?.();
            },
            onError: (error) =>
              toast.error(
                isConflictError(error)
                  ? t`A menu item named "${data.name}" already exists in this menu`
                  : getApiErrorMessage(error, t`Failed to create menu item`),
              ),
          });
        }
      }}
    />
  );
}
